import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, ArrowDown } from 'lucide-react';

const FullScreenHeroSlider = ({ 
  slides, 
  interval = 6000,
  showScrollIndicator = true 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [direction, setDirection] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, interval);
    return () => clearInterval(timer);
  }, [slides.length, interval, isPlaying]);

  // Preload images
  useEffect(() => {
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, [slides]);

  const goToPrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 1.05
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.95
    })
  };

  const currentSlide = slides[currentIndex];

  return (
    <div className="relative w-full h-[calc(100vh-72px)] overflow-hidden bg-[#0a0e27]">
      {/* Background Slider */}
      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 200, damping: 25 },
            opacity: { duration: 0.6 },
            scale: { duration: 0.8 }
          }}
          className="absolute inset-0"
        >
          {/* Background Image with Ken Burns effect */}
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: 10, ease: 'linear' }}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${currentSlide.image})`,
            }}
          />
          
          {/* Dark Overlay for better text readability */}
          <div className="absolute inset-0 bg-black/50" />
          
          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e27]/80 via-transparent to-[#0a0e27]/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-indigo-600/10" />
          
          {/* Animated Grid Pattern */}
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full">
              <defs>
                <pattern id="heroGrid" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                  <circle cx="40" cy="40" r="1" fill="#06B6D4" />
                  <line x1="0" y1="40" x2="80" y2="40" stroke="#06B6D4" strokeWidth="0.3" opacity="0.4" />
                  <line x1="40" y1="0" x2="40" y2="80" stroke="#06B6D4" strokeWidth="0.3" opacity="0.4" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#heroGrid)" />
            </svg>
          </div>

          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-primary/40 rounded-full"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                }}
                animate={{
                  y: [null, Math.random() * -200],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 4 + Math.random() * 4,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            <div className="max-w-4xl">
              {/* Category Badge */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`category-${currentIndex}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="inline-block mb-6"
                >
                  <span className="px-6 py-2 bg-primary/20 border border-primary/40 rounded-full text-primary text-sm font-semibold uppercase tracking-wider backdrop-blur-md shadow-lg shadow-primary/20">
                    {currentSlide.category}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Heading */}
              <AnimatePresence mode="wait">
                <motion.h1
                  key={`title-${currentIndex}`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -40 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold text-white mb-6 leading-[1.1] uppercase tracking-tight"
                  style={{ textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}
                  data-testid="hero-heading"
                >
                  {currentSlide.title}
                </motion.h1>
              </AnimatePresence>

              {/* Description */}
              <AnimatePresence mode="wait">
                <motion.p
                  key={`desc-${currentIndex}`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -40 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-lg md:text-xl lg:text-2xl text-gray-200 mb-10 leading-relaxed font-light max-w-3xl"
                  data-testid="hero-subheading"
                >
                  {currentSlide.description}
                </motion.p>
              </AnimatePresence>

              {/* CTA Buttons */}
              {currentSlide.buttons && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`buttons-${currentIndex}`}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -40 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex flex-wrap gap-4"
                  >
                    {currentSlide.buttons.map((button, idx) => (
                      <a
                        key={idx}
                        href={button.link}
                        data-testid={`hero-cta-${button.primary ? 'primary' : 'secondary'}`}
                        className={`px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-sm transition-all duration-300 ${
                          button.primary
                            ? 'bg-gradient-to-r from-primary to-indigo-500 text-black hover:shadow-2xl hover:shadow-primary/40 hover:scale-105 shine-effect'
                            : 'bg-white/10 text-white border-2 border-white/30 hover:bg-white/20 hover:border-primary/50 backdrop-blur-md'
                        }`}
                      >
                        {button.text}
                      </a>
                    ))}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-3"
          >
            <span className="text-xs text-white/60 uppercase tracking-widest">Scroll to Explore</span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ArrowDown size={24} className="text-primary" />
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Navigation Controls - Hidden on mobile, visible on md+ */}
      <div className="hidden md:flex absolute inset-y-0 left-0 right-0 items-center justify-between px-4 lg:px-8 z-20 pointer-events-none">
        <motion.button
          whileHover={{ scale: 1.1, x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={goToPrevious}
          data-testid="slider-prev-btn"
          className="w-14 h-14 lg:w-16 lg:h-16 bg-black/30 hover:bg-black/50 backdrop-blur-xl rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-primary/50 pointer-events-auto group"
        >
          <ChevronLeft size={28} className="text-white group-hover:text-primary transition-colors" strokeWidth={2.5} />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1, x: 5 }}
          whileTap={{ scale: 0.95 }}
          onClick={goToNext}
          data-testid="slider-next-btn"
          className="w-14 h-14 lg:w-16 lg:h-16 bg-black/30 hover:bg-black/50 backdrop-blur-xl rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-primary/50 pointer-events-auto group"
        >
          <ChevronRight size={28} className="text-white group-hover:text-primary transition-colors" strokeWidth={2.5} />
        </motion.button>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-6 left-4 right-4 md:left-auto md:right-8 z-20">
        <div className="flex flex-col md:flex-row items-center justify-center md:justify-end gap-3 md:gap-4">
          {/* Slide Indicators - Mobile Centered */}
          <div className="flex space-x-2 order-1 md:order-2">
            {slides.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                whileHover={{ scale: 1.2 }}
                data-testid={`slider-dot-${index}`}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'bg-primary w-10 md:w-12 h-3 shadow-lg shadow-primary/50'
                    : 'bg-white/40 hover:bg-white/60 w-3 h-3'
                }`}
              />
            ))}
          </div>

          {/* Play/Pause & Counter - Desktop Only */}
          <div className="hidden md:flex items-center space-x-3 order-2 md:order-1">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              data-testid="slider-play-pause"
              className="px-5 py-2.5 bg-black/40 hover:bg-black/60 backdrop-blur-xl rounded-full flex items-center space-x-2 transition-all border border-white/20 hover:border-primary/50 group"
            >
              {isPlaying ? (
                <Pause size={16} className="text-white group-hover:text-primary transition-colors" />
              ) : (
                <Play size={16} className="text-white group-hover:text-primary transition-colors" />
              )}
              <span className="text-sm text-white font-medium">{isPlaying ? 'Pause' : 'Play'}</span>
            </button>

            <div className="px-4 py-2.5 bg-black/40 backdrop-blur-xl rounded-full border border-white/20">
              <span className="text-sm text-white font-mono">
                {String(currentIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenHeroSlider;
